/**
 * Real estate (spec §12): value paths, carrying costs, rental income, and the
 * sale computation — Section 121 exclusion automated for primary residences,
 * depreciation recapture ESTIMATED for rentals (flagged as such in the UI).
 */
import type { FilingStatus, Property } from '../model/types';

/** Whether the household owns this property in `year`. Sale year counts as owned. */
export function ownedInYear(prop: Property, year: number): boolean {
  if (prop.ownership === 'rent') return false;
  if (prop.purchaseYear != null && year < prop.purchaseYear) return false;
  if (prop.plannedSaleYear != null && year > prop.plannedSaleYear) return false;
  return true;
}

/** Market value in `year`; `currentValue` is stated as of `startYear`. */
export function propertyValueInYear(
  prop: Property,
  year: number,
  startYear: number,
  housingAppreciation: number,
): number {
  const rate = prop.appreciationRateOverride ?? housingAppreciation;
  return prop.currentValue * Math.pow(1 + rate, year - startYear);
}

export interface CarryingCosts {
  total: number;
  propertyTax: number;
}

/** Annual carrying costs: fixed costs inflate with CPI; maintenance tracks value. */
export function carryingCosts(
  prop: Property,
  value: number,
  inflationFactor: number,
): CarryingCosts {
  const propertyTax = prop.propertyTaxAnnual * inflationFactor;
  const fixed = (prop.insuranceAnnual + prop.hoaAnnual) * inflationFactor;
  const maintenance = prop.maintenancePctOfValue * value;
  return { total: propertyTax + fixed + maintenance, propertyTax };
}

/** Net annual rental income (vacancy and expenses off the top). */
export function netRentalIncome(prop: Property, inflationFactor: number): number {
  const r = prop.rentalIncome;
  if (!r) return 0;
  return Math.max(0, (r.grossAnnual * (1 - r.vacancyPct) - r.expensesAnnual) * inflationFactor);
}

export interface SaleResult {
  grossValue: number;
  sellingCosts: number;
  /** Amount realized after selling costs, before any mortgage payoff. */
  amountRealized: number;
  /** Estimated straight-line depreciation taken (rentals only). */
  depreciationTaken: number;
  section121Exclusion: number;
  /** Gain taxed at LTCG rates (after exclusion, less recapture portion). */
  taxableLtcgGain: number;
  /** Recapture portion, taxed at 25% — an ESTIMATE (flag in the UI). */
  recaptureGain: number;
  recaptureTaxEstimate: number;
}

/**
 * Compute the tax picture of a sale in `saleYear`.
 *
 * - Primary residence: Section 121 excludes $250k single / $500k MFJ of gain.
 *   (Assumes the two-of-five-year ownership & use test is met — noted in UI.)
 * - Rentals: straight-line depreciation over 27.5y on 80% of basis (land
 *   doesn't depreciate) is estimated for the holding period, reduces basis,
 *   and is recaptured at 25%.
 */
export function computeSale(
  prop: Property,
  value: number,
  saleYear: number,
  startYear: number,
  filingStatus: FilingStatus,
): SaleResult {
  const sellingCosts = value * prop.sellingCostPct;
  const amountRealized = value - sellingCosts;

  let depreciationTaken = 0;
  if (prop.type === 'rental') {
    const holdStart = prop.purchaseYear ?? startYear;
    const yearsHeld = Math.max(0, saleYear - holdStart);
    const depreciable = prop.costBasis * 0.8;
    depreciationTaken = Math.min(depreciable, (depreciable / 27.5) * yearsHeld);
  }

  const adjustedBasis = prop.costBasis - depreciationTaken;
  let gain = amountRealized - adjustedBasis;
  if (gain <= 0) {
    // Losses on personal residences aren't deductible; rental losses are out
    // of scope for v1 — treat as zero tax either way.
    return {
      grossValue: value, sellingCosts, amountRealized,
      depreciationTaken, section121Exclusion: 0,
      taxableLtcgGain: 0, recaptureGain: 0, recaptureTaxEstimate: 0,
    };
  }

  let section121Exclusion = 0;
  if (prop.type === 'primary') {
    const cap = filingStatus === 'mfj' || filingStatus === 'qw' ? 500_000 : 250_000;
    section121Exclusion = Math.min(gain, cap);
    gain -= section121Exclusion;
  }

  const recaptureGain = Math.min(depreciationTaken, gain);
  const taxableLtcgGain = gain - recaptureGain;
  return {
    grossValue: value,
    sellingCosts,
    amountRealized,
    depreciationTaken,
    section121Exclusion,
    taxableLtcgGain,
    recaptureGain,
    recaptureTaxEstimate: recaptureGain * 0.25,
  };
}
